/**
 * Atelier Hollis, the shop's garment storefront (atelier-ui.ts).
 *
 * One room on the top floor of a half-collapsed department store, lit by a
 * patched skylight: walnut, pattern paper, brass, chalk, thread. Lighter grime
 * than the rest of the game, clutter is pins and chalk. Art is in
 * public/atelier/ (generated 2026-10-17, see assets/generated_items/atelier).
 *
 * Rules held here (design/gdd/ui-visual-style.md):
 *  - no backdrop-filter, no glass; every surface a material with a highlight;
 *  - tier is the OBJECT: paper tag, brass tag, gilt tag, never a coloured chip;
 *  - 44px tap targets, decoration on pseudo-elements only;
 *  - no teal; only the Golden Set wears a foil sheen;
 *  - motion is transform and opacity only, and every loop and one-shot has a
 *    reduced-motion fallback at the bottom of this file.
 *
 * NO BACKTICKS anywhere in this file, comments included, and no dollar-brace:
 * the export is one template literal (css-literal.test.ts enforces it).
 */
export default `
/* ── Tokens: the game's palette, warmed toward paper and walnut ─────────── */
#shop-ui .bv-shop-atelier {
  --atelier-paper: #d9c9a8;
  --atelier-brass: #c9a24a;
  --atelier-ink: #2a1d10;
  --atelier-walnut: #3a2614;
}

/* ── The storefront switch: two brass plates, above everything (#2) ────── */
.bv-shop-fronts { display: flex; gap: 8px; margin: 0 40px 8px 0; flex-wrap: wrap; }
.bv-shop-front {
  min-height: 44px; padding: 8px 16px; cursor: pointer;
  font: 700 0.95rem var(--font-display); letter-spacing: 0.06em; text-transform: uppercase;
  color: #2a1d10; border: 1px solid rgba(88,56,18,0.7); border-radius: 4px;
  background:
    radial-gradient(120% 90% at 20% 10%, rgba(255,240,200,0.55), transparent 55%),
    linear-gradient(180deg, #d8b46a 0%, #a8803e 55%, #6e4f22 100%);
  box-shadow: inset 0 1px 0 rgba(255,246,222,0.6), inset 0 -2px 0 rgba(60,38,12,0.6), 0 2px 5px rgba(0,0,0,0.5);
  opacity: 0.62; transform: translateY(2px);
  transition: opacity 180ms ease, transform 180ms ease;
}
.bv-shop-front-on { opacity: 1; transform: none; }
.bv-shop-front:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 2px; }

/* The folding fitting-screen that slides across on a swap (#2). */
#shop-ui .bv-win { overflow-x: hidden; }
#shop-ui .bv-shop-swapping::after {
  content: ''; position: absolute; inset: 0; z-index: 30; pointer-events: none;
  background:
    repeating-linear-gradient(90deg, rgba(0,0,0,0.28) 0 2px, transparent 2px 25%),
    linear-gradient(180deg, #e2d2ae, #b99d6a);
  transform: translateX(-102%);
  /* No fill: at rest it sits off to the LEFT, where it adds no scroll width.
     Held at its end frame (102% right) it widened the window to twice its size. */
  animation: atl-screen 350ms cubic-bezier(.6,.05,.3,1);
}
@keyframes atl-screen {
  0% { transform: translateX(-102%); }
  45%, 55% { transform: translateX(0); }
  100% { transform: translateX(102%); }
}

/* ── The Atelier window: stage left, rail right, both scroll inside ─────── */
#shop-ui .bv-win.bv-shop-atelier {
  display: flex; flex-direction: column;
  height: min(86vh, 760px); overflow: hidden;
  /* Same inset as the market plate (commerce.css.ts), so the storefront tabs
     do not jump when the shop and the Atelier swap. */
  padding: 12px 14px;
  background:
    radial-gradient(90% 60% at 30% 0%, rgba(255,232,180,0.16), transparent 60%),
    url(/clutter/grime.webp) center / cover,
    linear-gradient(170deg, #3b2817 0%, #24170c 60%, #1a1008 100%);
  background-blend-mode: normal, soft-light, normal;
}
#shop-ui .bv-shop-atelier #shop-header {
  display: flex; flex-wrap: wrap; align-items: center; gap: 4px 14px; padding-right: 36px;
}
#shop-ui .bv-shop-atelier .bv-shop-fronts { margin: 0; flex: none; }
#shop-ui .bv-shop-atelier .bv-win-body { flex: 1; min-height: 0; margin-top: 8px; }
.bv-atl-head {
  position: relative; display: flex; align-items: center; gap: 10px; flex: 1; min-width: 180px;
  padding: 4px 10px; border-radius: 4px; overflow: hidden;
  /* The atelier itself behind the sign: skylight, patched panes, pinned patterns. */
  background:
    linear-gradient(90deg, rgba(20,12,6,0.88), rgba(20,12,6,0.55) 60%, rgba(20,12,6,0.8)),
    url(/atelier/atelier-header-sm.webp) center 35% / cover;
}
.bv-atl-head > * { position: relative; }
/* The oil chandelier's warm flicker (#3): opacity only, irregular steps. */
.bv-atl-head > .bv-atl-glow {
  position: absolute; left: 38%; top: -40%; width: 30%; height: 180%; pointer-events: none;
  background: radial-gradient(closest-side, rgba(255,196,110,0.32), transparent);
  animation: atl-flicker 4.6s steps(1, end) infinite;
}
@keyframes atl-flicker {
  0% { opacity: 0.9; } 7% { opacity: 0.7; } 9% { opacity: 1; } 31% { opacity: 0.82; } 33% { opacity: 0.95; }
  58% { opacity: 0.75; } 61% { opacity: 1; } 83% { opacity: 0.86; }
}
@media (min-width: 1000px) {
  .bv-atl-head { background:
    linear-gradient(90deg, rgba(20,12,6,0.88), rgba(20,12,6,0.55) 60%, rgba(20,12,6,0.8)),
    url(/atelier/atelier-header.webp) center 35% / cover; }
}
.bv-atl-head .bv-win-title { margin: 0; color: var(--atelier-paper); }
.bv-atl-head .bv-win-sub { margin: 0; font-family: var(--font-note); color: var(--bone-dim); }
.bv-atl-wallet { display: flex; align-items: center; gap: 8px; margin: 0; }
.bv-atl-topup, .bv-atl-short {
  min-height: 44px; min-width: 44px; padding: 6px 12px; cursor: pointer;
  font: 0.85rem var(--font-note); color: var(--atelier-ink);
  border: none; border-radius: 2px;
  background:
    radial-gradient(80% 70% at 30% 20%, rgba(255,255,255,0.35), transparent 60%),
    linear-gradient(175deg, #e8dbbd, #cbb88f 70%, #b39e72);
  box-shadow: 0 2px 4px rgba(0,0,0,0.45);
  transform: rotate(-2deg);
}

/* The door bell over the sign (#1). */
.bv-atl-bell {
  width: 26px; height: 26px; flex: none;
  background: radial-gradient(circle at 40% 30%, #ffe6a8, #c9a24a 45%, #6e4f22 80%);
  border-radius: 50% 50% 40% 40%;
  box-shadow: 0 2px 3px rgba(0,0,0,0.5);
  transform-origin: 50% 0;
}
/* The pattern paper unrolling across the header (#1). */
.bv-atl-unroll {
  position: absolute; left: 0; right: 0; bottom: -4px; height: 3px; pointer-events: none;
  background: repeating-linear-gradient(90deg, var(--atelier-paper) 0 10px, transparent 10px 14px);
  opacity: 0.55; transform-origin: 0 50%;
}
/* Only while the window carries the one-shot entering class (set on each visit). */
.bv-atl-entering .bv-atl-bell { animation: atl-bell 450ms ease-out 1; }
.bv-atl-entering .bv-atl-unroll { animation: atl-unroll 450ms ease-out 1 both; }
@keyframes atl-bell {
  0% { transform: rotate(0); } 20% { transform: rotate(18deg); } 45% { transform: rotate(-14deg); }
  70% { transform: rotate(7deg); } 100% { transform: rotate(0); }
}
@keyframes atl-unroll { from { transform: scaleX(0); } to { transform: scaleX(1); } }

.bv-atl {
  position: relative; height: 100%;
  display: grid; grid-template-columns: minmax(230px, 32%) minmax(0, 1fr); gap: 12px;
}
.bv-atl > * { min-width: 0; }
.bv-atl-paused, .bv-atl-paused * { animation-play-state: paused !important; }

/* ── The Fitting Stage ───────────────────────────────────────────────────── */
.bv-atl-stage {
  position: relative; min-height: 0; overflow: hidden;
  display: flex; flex-direction: column;
  border-radius: 6px;
  background:
    linear-gradient(180deg, rgba(20,12,6,0) 55%, rgba(20,12,6,0.85) 100%),
    url(/atelier/fitting-stage.webp) center 30% / cover,
    #2a1b0e;
  box-shadow: inset 0 0 0 1px rgba(201,162,74,0.35), inset 0 0 40px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.5);
}
/* Skylight shaft (#3), light only: a slow breathe of opacity. */
.bv-atl-stage::before {
  content: ''; position: absolute; inset: -10% 10% 30% 25%; pointer-events: none; z-index: 1;
  background: linear-gradient(200deg, rgba(255,236,190,0.22), rgba(255,236,190,0) 70%);
  transform: skewX(-12deg);
  animation: atl-breathe 5s ease-in-out infinite alternate;
}
@keyframes atl-breathe { from { opacity: 0.55; } to { opacity: 1; } }
.bv-atl-stage-art { display: none; }
/* Wear and clutter: tailor's odds and ends hanging off the stage's corner. */
.bv-atl-stage::after {
  content: ''; position: absolute; top: -18px; right: -22px; width: 110px; height: 110px; z-index: 7;
  pointer-events: none; transform: rotate(14deg);
  background: url(/atelier/clutter-c.webp) center / contain no-repeat;
}
.bv-atl-preview { position: relative; flex: 1; min-height: 150px; z-index: 2; transform-origin: 50% 90%; }
.bv-atl-hint {
  position: absolute; top: 6px; left: 8px; z-index: 3; pointer-events: none;
  font: 0.7rem var(--font-note); color: var(--bone-dim); opacity: 0.8;
}
.bv-atl-flash {
  position: absolute; inset: 0; z-index: 3; pointer-events: none; opacity: 0;
  background: radial-gradient(50% 45% at 50% 45%, rgba(255,240,205,0.45), transparent 70%);
}
.bv-atl-wipe {
  position: absolute; inset: 0; z-index: 4; pointer-events: none; opacity: 0;
  background: linear-gradient(90deg, transparent, rgba(232,219,189,0.8) 48% 52%, transparent);
  transform: translateX(-100%);
}
/* Garment pop-in (#8) and the before/after wipe (#10). */
.bv-atl-pop { animation: atl-pop 180ms ease-out 1; }
@keyframes atl-pop { from { transform: scale(0.96); } to { transform: scale(1); } }
.bv-atl-flashing { animation: atl-flash 180ms ease-out 1; }
@keyframes atl-flash { 0% { opacity: 0; } 40% { opacity: 1; } 100% { opacity: 0; } }
.bv-atl-wiping { animation: atl-wipe 300ms ease-in-out 1; }
@keyframes atl-wipe {
  0% { opacity: 1; transform: translateX(-100%); } 100% { opacity: 1; transform: translateX(100%); }
}

/* Hollis, stitching in the corner, and his speech tag (#16). */
.bv-atl-hollis {
  position: absolute; left: 6px; bottom: 112px; z-index: 5; pointer-events: none;
  display: flex; align-items: flex-end; gap: 4px; max-width: calc(100% - 12px);
}
.bv-atl-portrait { width: 52px; height: auto; filter: drop-shadow(0 2px 3px rgba(0,0,0,0.6)); }
.bv-atl-needle {
  position: absolute; left: 30px; bottom: 18px; width: 20px; height: 20px;
  color: #d8c9a2; opacity: 0.9;
  animation: atl-stitch 1.2s steps(4, end) infinite;
}
@keyframes atl-stitch {
  0% { transform: translate(0, 0) rotate(-20deg); } 25% { transform: translate(4px, -5px) rotate(-10deg); }
  50% { transform: translate(8px, 0) rotate(-20deg); } 75% { transform: translate(4px, 3px) rotate(-30deg); }
  100% { transform: translate(0, 0) rotate(-20deg); }
}
.bv-atl-speech {
  margin: 0 0 30px; padding: 5px 9px; max-width: 170px;
  font: 0.78rem/1.3 var(--font-note); color: var(--atelier-ink);
  background:
    radial-gradient(90% 80% at 20% 15%, rgba(255,255,255,0.4), transparent 60%),
    linear-gradient(175deg, #efe3c7, var(--atelier-paper) 70%, #c2ae84);
  border-radius: 2px; box-shadow: 0 2px 4px rgba(0,0,0,0.5);
  transform: rotate(-1.5deg); transform-origin: 0 100%;
}
.bv-atl-speaking { animation: atl-speak 220ms ease-out 1; }
@keyframes atl-speak { from { opacity: 0; transform: rotate(-6deg) scale(0.9); } to { opacity: 1; transform: rotate(-1.5deg); } }

.bv-atl-stage-ui { position: relative; z-index: 6; padding: 6px 8px 8px; }
.bv-atl-pins { display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; }
.bv-atl-slotpin {
  position: relative; min-height: 44px; padding: 2px; border-radius: 4px;
  display: flex; flex-direction: column; align-items: center; justify-content: flex-end;
  font: 0.6rem var(--font-technical); color: var(--bone-dim);
  background: linear-gradient(180deg, rgba(18,12,6,0.6), rgba(18,12,6,0.85));
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.6);
  border: none;
}
.bv-atl-slotpin-on {
  cursor: pointer; color: var(--bone);
  background:
    radial-gradient(80% 60% at 30% 10%, rgba(255,240,200,0.25), transparent 60%),
    linear-gradient(180deg, #5a4020, #2c1d0e);
  animation: atl-pin-on 150ms ease-out 1;
}
/* The safety pin itself, clipped to the slot. */
.bv-atl-slotpin-on::before {
  content: ''; position: absolute; top: -3px; right: 4px; width: 14px; height: 6px;
  border: 2px solid #d9d2c0; border-radius: 6px; transform: rotate(-20deg);
}
.bv-atl-slotpin-art { width: 26px; height: 26px; object-fit: contain; }
.bv-atl-slotpin-off { animation: atl-pin-off 150ms ease-in 1 forwards; }
@keyframes atl-pin-on { from { transform: scale(0.7) rotate(-12deg); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes atl-pin-off { to { transform: translateY(-8px) rotate(18deg); opacity: 0; } }
.bv-atl-ba { display: flex; gap: 4px; margin-top: 6px; }
.bv-atl-ba-btn {
  flex: 1; min-height: 44px; cursor: pointer;
  font: 700 0.8rem var(--font-display); letter-spacing: 0.05em; text-transform: uppercase;
  color: var(--bone-dim); border: 1px solid rgba(201,162,74,0.3); border-radius: 4px;
  background: linear-gradient(180deg, #2c1e10, #1a1008);
}
.bv-atl-ba-btn[aria-pressed="true"] {
  color: var(--atelier-ink); border-color: rgba(88,56,18,0.8);
  background: radial-gradient(90% 80% at 30% 15%, rgba(255,240,200,0.5), transparent 60%), linear-gradient(180deg, #d8b46a, #9b7436);
}
.bv-atl-stage-note { margin: 6px 0 0; font: 0.75rem var(--font-note); color: var(--bone-dim); }
.bv-atl-delta { margin: 6px 0 0; font: 0.75rem/1.5 var(--font-technical); color: var(--bone); }
.bv-atl-d { display: inline-block; margin-right: 8px; white-space: nowrap; }
.bv-atl-d-up { color: #b8cc7a; }
.bv-atl-d-down { color: #e58a6c; }

/* Bottom-sheet grip: hidden in landscape, the sheet's handle on phones. */
.bv-atl-grip { display: none; }

/* ── The rail column ─────────────────────────────────────────────────────── */
.bv-atl-shop { position: relative; min-height: 0; overflow-x: hidden; overflow-y: auto; padding: 0 4px 12px 0; }

/* The Golden Set hero banner (#14): gilt frame, chasing bulbs, foil sheen. */
.bv-atl-hero {
  /* The frame's opening is inset about 9% at the sides and 24% top and
     bottom (measured on the art), so the content sits inside it. */
  position: relative; overflow: hidden; margin-bottom: 10px; padding: 40px 10% 36px;
  min-height: 180px;
  display: grid; grid-template-columns: minmax(150px, 40%) 1fr; gap: 12px; align-items: center;
  border-radius: 6px;
  background:
    url(/atelier/golden-hero-frame.webp) center / 100% 100% no-repeat,
    radial-gradient(70% 90% at 30% 30%, rgba(255,220,140,0.28), transparent 65%),
    linear-gradient(160deg, #3a2410, #1e1208);
}
.bv-atl-hero-foil {
  position: absolute; inset: 0; pointer-events: none; z-index: 1;
  background: linear-gradient(105deg, transparent 35%, rgba(255,236,170,0.35) 50%, transparent 65%);
  transform: translateX(-100%);
  animation: atl-foil 5s ease-in-out infinite;
}
@keyframes atl-foil { 0%, 55% { transform: translateX(-100%); } 85%, 100% { transform: translateX(100%); } }
.bv-atl-bulb {
  position: absolute; top: 5px; z-index: 2; width: 8px; height: 8px; border-radius: 50%;
  left: calc(10% + var(--i) * 16%);
  background: radial-gradient(circle at 40% 35%, #fff6d8, #ffc764 55%, #8a5a16);
  box-shadow: 0 0 6px rgba(255,200,100,0.8);
  opacity: 0.35;
  animation: atl-chase 1.2s steps(1, end) infinite;
  animation-delay: calc(var(--i) * 0.2s);
}
@keyframes atl-chase { 0% { opacity: 1; } 34% { opacity: 0.35; } }
.bv-atl-hero-mote, .bv-atl-mote {
  position: absolute; z-index: 2; pointer-events: none; width: 3px; height: 3px; border-radius: 50%;
  background: rgba(255,236,190,0.75); opacity: 0;
}
.bv-atl-hero-mote { left: calc(20% + var(--i) * 18%); bottom: 10%; animation: atl-mote 5s linear infinite; animation-delay: calc(var(--i) * -1.3s); }
@keyframes atl-mote {
  0% { opacity: 0; transform: translate(0, 0); } 20% { opacity: 0.9; }
  100% { opacity: 0; transform: translate(14px, -60px); }
}
.bv-atl-hero-fan { position: relative; z-index: 3; height: 110px; }
.bv-atl-hero-text { position: relative; z-index: 3; }
.bv-atl-hero-title { margin: 0; font: 700 1.3rem var(--font-display-stencil); color: #ffd98a; letter-spacing: 0.04em; text-shadow: 0 2px 2px rgba(0,0,0,0.7); }
.bv-atl-hero-sub { margin: 2px 0 6px; font: 0.8rem var(--font-note); color: var(--atelier-paper); }
.bv-atl-hero-price { margin: 0 0 6px; font: 1.1rem var(--font-technical); color: var(--bone); }

/* The rail of hanger tabs (#4): the bar is the brass pipe, the tabs hang off it. */
.bv-atl-rail {
  position: sticky; top: 0; z-index: 8; display: flex; gap: 4px; padding: 14px 4px 6px; margin-bottom: 8px;
  background:
    url(/atelier/hanger-rail.webp) center top / auto 18px repeat-x,
    linear-gradient(180deg, #2a1b0d, #1c1209 85%, rgba(28,18,9,0));
}
.bv-atl-tab {
  position: relative; flex: 1; min-width: 44px; min-height: 48px; cursor: pointer;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  font: 700 0.72rem var(--font-display); letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--atelier-ink); border: none; border-radius: 2px 2px 6px 6px;
  background:
    radial-gradient(80% 70% at 30% 15%, rgba(255,255,255,0.35), transparent 60%),
    linear-gradient(175deg, #e3d3b0, var(--atelier-paper) 60%, #b59f72);
  box-shadow: 0 2px 4px rgba(0,0,0,0.5);
  transform: translateY(3px) rotate(calc((var(--i) - 3) * 0.6deg)); opacity: 0.72;
  transform-origin: 50% 0;
  transition: transform 200ms ease, opacity 200ms ease;
}
/* The wire hanger hook each tab hangs from. */
.bv-atl-tab::before {
  content: ''; position: absolute; top: -10px; left: 50%; width: 10px; height: 10px; margin-left: -5px;
  border: 2px solid #b9b2a2; border-bottom: none; border-radius: 8px 8px 0 0;
}
.bv-atl-tab-on { opacity: 1; transform: none; }
.bv-atl-tab:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 2px; }
.bv-atl-tab-icon {
  width: 22px; height: 22px; background-color: var(--atelier-ink);
  -webkit-mask: var(--icon) center / contain no-repeat; mask: var(--icon) center / contain no-repeat;
}
.bv-atl-tab-changed .bv-atl-tab { animation: atl-clack 250ms ease-out both; animation-delay: calc(var(--i) * 30ms); }
@keyframes atl-clack { from { transform: translateX(-10px) rotate(-4deg); } 70% { transform: translateX(1px) rotate(1deg); } }

/* ── Cards: garments hanging on a pin ────────────────────────────────────── */
.bv-atl-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(156px, 1fr)); gap: 14px 10px; }
.bv-atl-grid-sets { grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); }
.bv-atl-card {
  position: relative; padding: 14px 10px 10px; border-radius: 3px;
  display: flex; flex-direction: column; gap: 4px;
  color: var(--atelier-ink);
  transform-origin: 50% 0;
}
.bv-atl-tab-changed .bv-atl-card { animation: atl-hang 600ms ease-out both; animation-delay: calc(var(--i) * 25ms); }
.bv-atl-card:hover, .bv-atl-card:focus-within { animation: atl-hang 600ms ease-out 1; }
@keyframes atl-hang {
  0% { transform: rotate(1.2deg); } 30% { transform: rotate(-1deg); } 55% { transform: rotate(0.6deg); }
  80% { transform: rotate(-0.25deg); } 100% { transform: rotate(0); }
}
/* Paper tag (common), brass tag (uncommon), gilt tag (rare): the material is the tier. */
.bv-atl-tier-paper, .bv-atl-tier-brass, .bv-atl-tier-gilt {
  color: #1f150a;
  /* Inside the tag's plain area: clear of the twine on top and the notched rim. */
  padding: 22% 11% 9%;
  background: var(--tag) center / 100% 100% no-repeat;
  filter: drop-shadow(0 3px 5px rgba(0,0,0,0.55));
}
/* The garment-tag frames (generated 2026-10-17): the twine loop is the hanger. */
.bv-atl-tier-paper { --tag: url(/atelier/tag-paper.webp); }
.bv-atl-tier-brass { --tag: url(/atelier/tag-brass.webp); }
.bv-atl-tier-gilt { --tag: url(/atelier/tag-gilt.webp); padding-left: 14%; padding-right: 14%; }
.bv-atl-tier-paper .bv-atl-pin, .bv-atl-tier-brass .bv-atl-pin, .bv-atl-tier-gilt .bv-atl-pin { display: none; }
.bv-atl-tier-gold { color: #1f150a; background: radial-gradient(90% 60% at 25% 10%, rgba(255,250,220,0.75), transparent 55%), linear-gradient(170deg, #ffe7a0, #d9a93c 45%, #7d5512); box-shadow: inset 0 0 0 2px rgba(255,236,170,0.7), 0 4px 12px rgba(0,0,0,0.6); }
/* The pin the garment hangs from. */
.bv-atl-pin {
  position: absolute; top: -6px; left: 50%; width: 12px; height: 12px; margin-left: -6px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #fff, #c7c0b0 40%, #6b665c);
  box-shadow: 0 2px 2px rgba(0,0,0,0.6);
}
.bv-atl-card-top { position: relative; display: flex; justify-content: center; height: 92px; }
.bv-atl-card-art { max-height: 92px; max-width: 100%; object-fit: contain; filter: drop-shadow(0 4px 4px rgba(0,0,0,0.45)); }
.bv-atl-card-noart { font: 2rem var(--font-display); opacity: 0.4; }
.bv-atl-tag { min-height: 0; }
.bv-atl-name { margin: 0; font: 700 0.92rem/1.15 var(--font-display); letter-spacing: 0.02em; }
.bv-atl-perk { margin: 2px 0 0; font: 0.72rem/1.3 var(--font-hand); opacity: 0.9; }
.bv-atl-owned-note { margin: 4px 0 0; font: 0.72rem/1.3 var(--font-note); }
/* The price on a string tag (#6 swings it). */
.bv-atl-price {
  align-self: flex-start; position: relative; margin-top: 2px; padding: 3px 8px 3px 14px;
  font: 0.9rem var(--font-technical); color: var(--bone);
  background: linear-gradient(175deg, #3a2614, #22160b);
  border-radius: 2px 8px 8px 2px; transform-origin: 0 50%;
  display: flex; align-items: center; gap: 6px;
}
.bv-atl-price::before {
  content: ''; position: absolute; left: 4px; top: 50%; width: 5px; height: 5px; margin-top: -2px;
  border-radius: 50%; background: #e8dbbd;
}
.bv-atl-card:hover .bv-atl-price { animation: atl-swing 500ms ease-out 1; }
@keyframes atl-swing { 0% { transform: rotate(0); } 30% { transform: rotate(-7deg); } 60% { transform: rotate(4deg); } 100% { transform: rotate(0); } }
.bv-atl-was { font-size: 0.75rem; opacity: 0.65; }
.bv-atl-only { font: 0.7rem var(--font-note); opacity: 0.8; }
.bv-atl-short { position: absolute; top: 8px; right: 6px; z-index: 3; min-height: 36px; padding: 4px 8px; font-size: 0.75rem; }
.bv-atl-acts { display: flex; gap: 6px; margin-top: auto; padding-top: 4px; }
.bv-atl-btn {
  flex: 1; min-height: 44px; cursor: pointer; border: none; border-radius: 3px;
  font: 700 0.85rem var(--font-display); letter-spacing: 0.06em; text-transform: uppercase;
}
.bv-atl-fit {
  color: #2a1d10;
  background:
    repeating-linear-gradient(90deg, rgba(60,40,10,0.55) 0 1px, transparent 1px 8px) left bottom / 100% 6px no-repeat,
    radial-gradient(80% 70% at 30% 20%, rgba(255,255,255,0.3), transparent 60%),
    linear-gradient(180deg, #f1d675, #d4b24a 60%, #a8862c);
  box-shadow: 0 2px 4px rgba(0,0,0,0.45);
}
.bv-atl-buy {
  color: #f6ead0;
  background:
    radial-gradient(80% 70% at 30% 15%, rgba(255,220,170,0.3), transparent 60%),
    linear-gradient(180deg, #7a4a24, #4f2d14 60%, #33190a);
  box-shadow: inset 0 1px 0 rgba(255,220,170,0.25), 0 2px 4px rgba(0,0,0,0.5);
}
.bv-atl-btn:active:not(:disabled) { transform: translateY(1px); }
.bv-atl-btn:disabled { cursor: default; filter: grayscale(0.7) brightness(0.75); box-shadow: none; }
.bv-atl-btn:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 2px; }

/* Owned: dims, and the wardrobe stamp sits across it (#17). */
.bv-atl-owned .bv-atl-card-top, .bv-atl-owned .bv-atl-fan { opacity: 0.6; }
.bv-atl-stamp {
  position: absolute; top: 34px; left: 50%; width: 80%; max-width: 170px; margin-left: -40%;
  pointer-events: none; transform: rotate(-8deg); opacity: 0.92;
}

/* Fit: the tape measure unrolls across the card (#7). */
.bv-atl-tape {
  position: absolute; left: 6px; right: 6px; top: 46%; height: 14px; z-index: 4; pointer-events: none;
  background: url(/atelier/tape-measure.webp) left center / auto 100% repeat-x;
  transform: scaleX(0); transform-origin: 0 50%; opacity: 0;
}
.bv-atl-taping .bv-atl-tape { animation: atl-tape 250ms ease-out 1 both, atl-fade 250ms ease-in 250ms 1 forwards; }
@keyframes atl-tape { from { transform: scaleX(0); opacity: 1; } to { transform: scaleX(1); opacity: 1; } }
@keyframes atl-fade { to { opacity: 0; } }

/* Buy: stitch line round the card, then SOLD slams (#11). */
.bv-atl-selling::after {
  content: ''; position: absolute; inset: 3px; z-index: 5; pointer-events: none; border-radius: 3px;
  outline: 2px dashed rgba(42,29,16,0.75); outline-offset: -2px;
  animation: atl-stitchline 300ms steps(6, end) 1 both, atl-fade 200ms ease-in 500ms 1 forwards;
}
@keyframes atl-stitchline { from { clip-path: inset(0 100% 100% 0); } to { clip-path: inset(0 0 0 0); } }
.bv-atl-selling::before {
  content: ''; position: absolute; top: 30%; left: 50%; width: 150px; height: 70px; margin-left: -75px; z-index: 6;
  background: url(/atelier/stamps/stamp-sold.svg) center / contain no-repeat; pointer-events: none;
  animation: atl-slam 260ms cubic-bezier(.3,1.6,.5,1) 300ms 1 both, atl-fade 300ms ease-in 1600ms 1 forwards;
}
@keyframes atl-slam { from { transform: scale(1.6) rotate(-8deg); opacity: 0; } to { transform: scale(1) rotate(-8deg); opacity: 1; } }

/* Tissue paper folds down over the card between the stitch and the stamp. */
.bv-atl-tissue {
  position: absolute; inset: 0; z-index: 5; pointer-events: none; opacity: 0;
  background: linear-gradient(170deg, rgba(246,240,226,0.92), rgba(226,214,190,0.85));
  transform: scaleY(0); transform-origin: 50% 0;
}
.bv-atl-selling .bv-atl-tissue { animation: atl-tissue 420ms ease-out 260ms 1 both; }
@keyframes atl-tissue {
  0% { opacity: 0.9; transform: scaleY(0); } 60% { opacity: 0.9; transform: scaleY(1); } 100% { opacity: 0; transform: scaleY(1); }
}
.bv-atl-puff {
  position: absolute; left: 0; top: 0; width: 80px; height: 40px; opacity: 0; pointer-events: none;
  background:
    radial-gradient(closest-side at 20% 60%, rgba(220,205,175,0.7), transparent),
    radial-gradient(closest-side at 50% 40%, rgba(220,205,175,0.6), transparent),
    radial-gradient(closest-side at 80% 65%, rgba(220,205,175,0.7), transparent);
}
.bv-atl-puffing { animation: atl-puff 400ms ease-out 560ms 1; }
@keyframes atl-puff { 0% { opacity: 0.9; scale: 0.6; } 100% { opacity: 0; scale: 1.4; } }

/* Set bought: ribbon wraps it, the lid pops (#15). */
.bv-atl-ribbon {
  position: absolute; inset: 0; z-index: 5; pointer-events: none; opacity: 0;
  background:
    linear-gradient(90deg, transparent 46%, #8c1f1a 46% 54%, transparent 54%),
    linear-gradient(0deg, transparent 44%, #8c1f1a 44% 56%, transparent 56%);
}
.bv-atl-boxing .bv-atl-ribbon { animation: atl-ribbon 900ms ease-out 1 both; }
@keyframes atl-ribbon {
  0% { opacity: 0; transform: scale(1.2); } 40% { opacity: 0.95; transform: scale(1); }
  70% { transform: translateY(-4px); } 100% { opacity: 0; transform: translateY(0); }
}

/* Not enough Gold: the card shakes, the Top up tag wiggles (#13). */
.bv-atl-shaking { animation: atl-shake 300ms linear 1; }
.bv-atl-shaking .bv-atl-short { animation: atl-wiggle 300ms ease-in-out 1; }
@keyframes atl-shake { 0%, 100% { transform: translateX(0); } 20%, 60% { transform: translateX(-4px); } 40%, 80% { transform: translateX(4px); } }
@keyframes atl-wiggle { 0%, 100% { transform: rotate(-2deg); } 50% { transform: rotate(6deg); } }
.bv-atl-short-pulse { outline: 3px solid #e58a6c; outline-offset: 2px; }

/* Set cards: the pieces fanned like a mannequin's outfit. */
.bv-atl-fan { position: relative; height: 96px; }
.bv-atl-fan-piece {
  position: absolute; top: 4px; left: 50%; width: 56px; height: 56px; margin-left: -28px;
  transform: translateX(calc((var(--i) - (var(--n) - 1) / 2) * 30px)) rotate(calc((var(--i) - (var(--n) - 1) / 2) * 7deg));
  transform-origin: 50% 120%;
}
.bv-atl-hero-fan .bv-atl-fan-piece { width: 64px; height: 64px; margin-left: -32px; top: 18px; }
.bv-atl-fan-art { width: 100%; height: 100%; object-fit: contain; filter: drop-shadow(0 3px 3px rgba(0,0,0,0.5)); }

/* Scroll: the tape-measure ruler (#18). Stuck to the bottom of the column. */
.bv-atl-ruler {
  position: sticky; bottom: 0; height: 8px; margin-top: 10px;
  background: url(/atelier/tape-measure.webp) left center / auto 100% repeat-x; opacity: 0.35;
}
.bv-atl-ruler i {
  position: absolute; inset: 0; display: block; transform-origin: 0 50%;
  background: url(/atelier/tape-measure.webp) left center / auto 100% repeat-x;
  opacity: 1; transform: scaleX(0);
}

/* Particles: five skylight motes, three ingot sparks; with the hero's four, twelve. */
.bv-atl-fx { position: absolute; inset: 0; pointer-events: none; z-index: 9; }
.bv-atl-mote { left: calc(6% + var(--i) * 5%); top: 28%; animation: atl-mote 6s linear infinite; animation-delay: calc(var(--i) * -1.1s); }
.bv-atl-spark {
  position: absolute; right: 12%; top: 40%; width: 7px; height: 4px; border-radius: 1px; opacity: 0;
  background: linear-gradient(90deg, #fff1b8, #e0a93a);
}
.bv-atl-sparking { animation: atl-spark 500ms ease-in 1; animation-delay: calc(var(--i) * 60ms); }
@keyframes atl-spark { 0% { opacity: 1; transform: translate(0, 0); } 100% { opacity: 0; transform: translate(60px, -260px) rotate(40deg); } }

/* ── Phones (below 720px): the stage is a bottom sheet ──────────────────── */
@media (max-width: 719px) {
  #shop-ui .bv-win.bv-shop-atelier { height: 92vh; height: 92dvh; padding: 10px; }
  .bv-atl { grid-template-columns: 1fr; }
  .bv-atl-shop { padding-bottom: 64px; }
  .bv-atl-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 8px; }
  .bv-atl-grid-sets { grid-template-columns: 1fr; }
  .bv-atl-hero { grid-template-columns: 1fr; padding: 34px 10% 30px; background-size: 100% 100%; }
  .bv-atl-rail { display: grid; grid-template-columns: repeat(4, 1fr); }
  .bv-atl-tab-label { font-size: 0.62rem; }
  .bv-atl-stage {
    position: absolute; left: 0; right: 0; bottom: 0; z-index: 20; height: 56px;
    transition: height 250ms ease;
    border-radius: 10px 10px 0 0;
  }
  .bv-atl-sheet-open .bv-atl-stage { height: 78%; }
  .bv-atl-grip {
    display: flex; align-items: center; justify-content: center; min-height: 52px; width: 100%;
    position: relative; z-index: 7; cursor: pointer; border: none; background: transparent;
    font: 700 0.85rem var(--font-display); letter-spacing: 0.06em; text-transform: uppercase; color: var(--atelier-paper);
  }
  .bv-atl-grip::before { content: ''; position: absolute; top: 6px; width: 40px; height: 4px; border-radius: 2px; background: rgba(217,201,168,0.6); }
  .bv-atl-stage:not(.x) .bv-atl-hollis { bottom: 120px; }
  .bv-atl-portrait { width: 40px; }
  .bv-atl-stage::after { width: 64px; height: 64px; top: -6px; right: -8px; }
}

/* ── Short landscape (844x390): every pixel of height counts ───────────── */
@media (max-height: 460px) {
  #shop-ui .bv-win.bv-shop-atelier { height: 94vh; height: 94dvh; padding: 10px 12px; }
  .bv-atl-head .bv-win-sub { display: none; }
  .bv-atl-head .bv-win-title { font-size: 1.1rem; }
  .bv-shop-front { min-height: 44px; padding: 6px 10px; font-size: 0.8rem; }
  /* The hero shrinks to a strip so the first row of cards is above the fold. */
  .bv-atl-hero { padding: 18px 10% 14px; min-height: 0; grid-template-columns: 34% 1fr; }
  .bv-atl-hero-fan { height: 54px; }
  .bv-atl-hero-fan .bv-atl-fan-piece { width: 44px; height: 44px; margin-left: -22px; top: 6px; }
  .bv-atl-hero-sub { display: none; }
  .bv-atl-hero-title { font-size: 1rem; }
  .bv-atl-hero-text { display: grid; grid-template-columns: auto auto; align-items: center; gap: 2px 10px; }
  .bv-atl-hero-text .bv-atl-acts { grid-column: 1 / -1; padding-top: 0; }
  .bv-atl-hero-price { margin: 0; font-size: 0.9rem; }
  .bv-atl-bulb { top: 3px; width: 6px; height: 6px; }
  /* Hollis only speaks up when something happens: his idle line covered the survivor. */
  .bv-atl-speech[data-idle] { display: none; }
  /* Decoration drops on a short screen: the sticker sat on the pin column. */
  .bv-atl-stage::after { display: none; }
  /* The stage splits: survivor on the left, its controls in a column on the
     right, so the pins and the delta line stop eating the survivor's height. */
  .bv-atl-stage { display: grid; grid-template-columns: minmax(0, 1fr) 104px; }
  .bv-atl-preview { grid-column: 1; grid-row: 1; min-height: 0; }
  .bv-atl-flash, .bv-atl-wipe { grid-column: 1; grid-row: 1; }
  .bv-atl-stage-ui { grid-column: 2; grid-row: 1; overflow-y: auto; padding: 6px 4px; background: rgba(18,12,6,0.55); }
  .bv-atl-pins { grid-template-columns: repeat(2, 1fr); }
  .bv-atl-ba { flex-direction: column; }
  .bv-atl-ba-btn { min-height: 40px; }
  .bv-atl-delta, .bv-atl-stage-note { font-size: 0.66rem; }
  .bv-atl-d { white-space: normal; }
  .bv-atl-portrait, .bv-atl-needle { display: none; }
  .bv-atl-hollis { top: 18px; bottom: auto; left: 4px; }
  .bv-atl-speech { margin: 0; max-width: 120px; font-size: 0.66rem; padding: 3px 6px; }
}

/* ── Reduced motion: every change instant, every loop a still frame ─────── */
@media (prefers-reduced-motion: reduce) {
  .bv-atl *, .bv-atl *::before, .bv-atl *::after, .bv-atl::before,
  .bv-atl-entering .bv-atl-bell, .bv-atl-entering .bv-atl-unroll, #shop-ui .bv-shop-swapping::after {
    animation: none !important; transition: none !important;
  }
  .bv-atl-mote, .bv-atl-hero-mote, .bv-atl-spark, .bv-atl-puff { display: none; }
  .bv-atl-head > .bv-atl-glow { opacity: 0.9; }
  .bv-atl-hero-foil { transform: none; opacity: 0.5; }
  .bv-atl-bulb { opacity: 0.85; }
  .bv-atl-stage::before { opacity: 0.8; }
  .bv-atl-unroll { transform: none; }
}

/* == One shop window, real tabs (Janeks, 2026-10-19) =========================
   "Atelier and the regular shop need to be in one, same size. Change the tabs
   buttons to real tab style": both faces share one width (SHOP_W in shop-ui)
   and the height below, and the storefront switch is the Survivor Record's
   metal-tag tab strip instead of three brass buttons. */
#shop-ui .bv-win.bv-shop-plate {
  display: flex; flex-direction: column; overflow: hidden;
  height: min(86vh, 760px); height: min(86dvh, 760px); max-height: none;
}
#shop-ui .bv-shop-plate #shop-header { flex: none; }
#shop-ui .bv-shop-plate .bv-win-body { flex: 1 1 auto; min-height: 0; }
#shop-ui .bv-shop-plate:not(.bv-shop-atelier) .bv-win-body { overflow-y: auto; }
#shop-ui .bv-shop-plate .bv-shop-body .bv-shop-grid { max-height: none; }

#shop-ui .bv-shop-plate .bv-shop-fronts {
  display: flex; flex-wrap: nowrap; align-items: flex-end; gap: 4px;
  margin: 0 46px 8px 0; padding: 0;
  border-bottom: 2px solid #120d08; box-shadow: 0 1px 0 rgba(255,215,150,0.1);
}
#shop-ui .bv-shop-atelier .bv-shop-fronts { flex: 1 1 100%; margin: 0 0 4px; }
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front {
  position: relative; min-height: 40px; padding: 7px 16px 8px; margin-bottom: -2px;
  font: 700 13px var(--font-display); letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--bone-dim); text-shadow: 0 1px 0 rgba(0,0,0,0.8);
  border: 1px solid #3a2c19; border-bottom: none; border-radius: var(--radius-well) var(--radius-well) 0 0;
  background: linear-gradient(180deg, #2a2014, #150f09);
  box-shadow: none; opacity: 1; transform: none; filter: none;
  transition: transform 0.14s ease, color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;
}
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front:not(.bv-shop-front-soon):hover {
  transform: translateY(-2px); color: var(--brass-hot);
  background: linear-gradient(180deg, #3d2d1b, #1b130b);
  box-shadow: 0 -3px 8px rgba(255,189,92,0.14);
}
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: -2px; }
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-on {
  color: var(--brass-hot);
  background: linear-gradient(180deg, #4a3822, #1d140b 70%);
  border-color: #6b5230; border-top: 2px solid var(--brass-hot);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.25), 0 -4px 10px rgba(255,189,92,0.12);
  transform: translateY(-1px);
}
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-soon {
  display: inline-flex; flex-direction: row; align-items: center; gap: 6px;
  opacity: 0.5; cursor: not-allowed;
}
#shop-ui .bv-shop-plate .bv-shop-soon-tag { margin: 0; color: var(--bone-dim); background: rgba(0,0,0,0.45); border: 1px solid #3a2c19; }

@media (max-width: 719px) {
  #shop-ui .bv-win.bv-shop-plate { height: 92vh; height: 92dvh; padding: 10px; }
}
@media (max-width: 640px) {
  #shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front {
    flex: 1 1 0; min-width: 0; min-height: 44px; padding: 4px 3px; font-size: 0.64rem;
    letter-spacing: 0.02em; line-height: 1.1; overflow-wrap: anywhere;
  }
  #shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-soon { flex-direction: column; gap: 1px; }
}
@media (max-height: 460px) {
  #shop-ui .bv-win.bv-shop-plate { height: 94vh; height: 94dvh; padding: 10px 12px; }
  #shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front { min-height: 44px; padding: 6px 10px; font-size: 0.8rem; }
}
@media (prefers-reduced-motion: reduce) {
  #shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front { transition: none; }
}

/* Hollis's lines, for screen readers only (portrait and bubble removed 2026-10-19). */
.bv-atl-status { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

/* == Whole-shop scroll (Janeks, 2026-10-19): only the storefront tabs stay frozen ==
   Market: the wallet, offers, category tabs and cards all scroll in the body;
   the card grid no longer scrolls on its own. Atelier: the stage (live 3D
   preview) stays in view and the right-hand column, heading and wallet
   included, is the one scroller. */
#shop-ui .bv-shop-plate:not(.bv-shop-atelier) .bv-win-body { overflow-y: auto; overscroll-behavior: contain; }
#shop-ui .bv-shop-plate:not(.bv-shop-atelier) .bv-shop-body .bv-shop-grid {
  flex: none; min-height: 0; max-height: none; overflow: visible;
}
#shop-ui .bv-shop-plate .bv-shop-body > * { flex: none; }
/* The offer art hangs off the right edge of its card. Left to overflow it made
   the body 5px wider than its box, so the pane could scroll sideways and a
   nudge clipped the first card's left border. Room is kept for the art and the
   pane never scrolls sideways. */
#shop-ui .bv-shop-plate:not(.bv-shop-atelier) .bv-win-body { overflow-x: hidden; }
#shop-ui .bv-shop-plate .bv-shop-offers { padding-right: 10px; }
#shop-ui .bv-shop-plate #shop-header { padding-right: 46px; }
#shop-ui .bv-shop-plate #shop-header .bv-shop-fronts { margin-right: 0; }
.bv-atl-head-slot { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 14px; margin: 0 0 10px; }
.bv-atl-head-slot .bv-atl-head { flex: 1 1 260px; }
.bv-atl-head-slot .bv-shop-wallet { margin: 0; }
`;
